import { createTheme, adaptV4Theme } from '@mui/material/styles';
import palette from './palette.scss';

const PALETTE = {
  primary: {
    light: palette.primaryLight,
    main: palette.primaryMain,
    dark: '#1945d5',
    contrastText: '#ffffff'
  },
  secondary: {
    light: '#9f8cff',
    main: '#665eff',
    dark: '#1a33cb',
    contrastText: '#ffffff'
  },
  tertiary: {
    light: '#fff179',
    main: '#ffee58',
    dark: '#b2a63d',
    contrastText: '#ffffff'
  }
};

export const MUI_THEME_OPTIONS = {
  typography: {
    htmlFontSize: 10,
    fontFamily: "'Roboto', 'Noto Sans TC', 'Noto Sans SC', sans-serif !important",
    // Headings use MUI v4 default rem values. With html { font-size: 62.5% } → 1rem = 10px,
    // these render at the same pixel sizes as the original v4 app:
    //   h1 6rem=60px  h2 3.75rem=37.5px  h3 3rem=30px  h4 2.125rem=21.25px
    //   h5 1.5rem=15px  h6 1.25rem=12.5px
    // (Previous values 9.6rem/6rem/4.8rem/… were wrongly calibrated assuming a 16px html
    //  base; the v4 app already ran at 10px, so those produced headings 1.6× too large.)
    // Note: h4 and h6 are further overridden by MuiTypography overrides below.
    // Body/subtitle/caption: intentionally larger than v4 defaults for mobile readability.
    h1: { fontSize: '6rem' }, // 60px
    h2: { fontSize: '3.75rem' }, // 37.5px
    h3: { fontSize: '3rem' }, // 30px
    h4: { fontSize: '2.125rem' }, // 21.25px (overridden to 3.125rem by MuiTypography.h4)
    h5: { fontSize: '1.5rem' }, // 15px
    h6: { fontSize: '1.25rem' }, // 12.5px (overridden to 1.8rem by MuiTypography.h6)
    subtitle1: { fontSize: '1.6rem' }, // 16px
    subtitle2: { fontSize: '1.4rem' }, // 14px
    body1: { fontSize: '1.4rem' }, // 14px
    body2: { fontSize: '1.4rem' }, // 14px
    caption: { fontSize: '1.2rem' }, // 12px
    overline: { fontSize: '1.2rem' } // 12px
  },
  palette: PALETTE,
  fontFamily: "'Roboto', 'Noto Sans TC', 'Noto Sans SC', sans-serif !important",
  overrides: {
    MuiButton: {
      root: {
        textTransform: 'unset',
        fontSize: 'inherit',
        padding: '5px 12px',
        lineHeight: '1.5'
      },
      textPrimary: {
        fontSize: 'inherit',
        color: '#3966f8'
      },
      outlinedPrimary: {
        fontSize: 'inherit',
        border: '1px solid #3966f8cc',
        color: '#3966f8'
      },
      outlined: {
        padding: '5px 12px'
      }
    },
    // MuiDrawer overrides moved to the v9-native components block below — the
    // legacy `paperAnchorLeft` slot key is silently dropped when passed through
    // adaptV4Theme in v9 (only `paper` survives), leaving the left side-nav with
    // no fixed width and falling back to its natural content width (~170px).
    MuiInput: {
      root: {
        'font-size': 'inherit'
      },
      input: {
        'box-sizing': 'border-box',
        padding: '0px 6px'
      },
      underline: {
        '&:after': {
          'border-bottom': '2px solid #3966f8'
        }
      },
      formControl: {
        'min-height': '18px'
      }
    },
    MuiOutlinedInput: {
      root: {
        'font-size': 'inherit'
      }
    },
    MuiFormControl: {
      root: {
        width: '100%',
        'font-size': 'inherit'
      }
    },
    MuiFormLabel: {
      root: {},
      focused: {}
    },
    MuiInputLabel: {
      root: {
        'font-size': 'inherit',
        'font-family': 'inherit',
        'font-weight': 'inherit'
      }
      // shrink & formControl transforms moved to components section
      // so they only apply to standard variant (not outlined)
    },
    MuiMenuItem: {
      root: {
        'font-size': 'inherit'
      }
    },
    MuiSwitch: {
      colorPrimary: {
        color: PALETTE.primary.main
      }
      // colorPrimary: {
      //   '& $thumb': {
      //     backgroundColor: PALETTE.primary.main,
      //   },
      //   thumb: {}
      // },
      //   iconChecked: {
      //     color: '#2196f3'
      //   },
      //   bar: {},
      //   checked: {
      //     '& + $bar': {
      //       backgroundColor: '#2196f3 !important'
      //     }
      //   }
    },
    MuiTouchRipple: {
      // child: {
      // 	backgroundColor: '#2196f3 !important',
      // },
    },
    // MuiSwitchBase: {
    //   root: {
    //     height: '29px'
    //   }
    // },
    MuiCheckbox: {
      root: {},
      checked: {},
      colorPrimary: {
        color: '#3966f8 !important'
      },
      colorSecondary: {
        // color: '#665eff !important'
        color: '#3966f8 !important'
      }
    },
    MuiSnackbar: {
      root: {
        'z-index': '10000'
      }
    },
    MuiSnackbarContent: {
      message: {
        'font-size': '1.25rem'
      }
    },
    // x-date-pickers v9 split the generic v4 MuiPickersToolbar.toolbar slot into
    // per-picker components (DatePicker / TimePicker / DateTimePicker), each with
    // a `root` slot. Apply the v4 height:auto + primary-blue background to all 3.
    MuiDatePickerToolbar: {
      root: {
        height: 'auto',
        backgroundColor: '#3966f8',
        color: '#fff',
        '& .MuiPickersToolbar-title': { color: '#fff' }
      }
    },
    MuiTimePickerToolbar: {
      root: {
        height: 'auto',
        backgroundColor: '#3966f8',
        color: '#fff',
        '& .MuiPickersToolbar-title': { color: '#fff' }
      }
    },
    MuiDateTimePickerToolbar: {
      root: {
        height: 'auto',
        backgroundColor: '#3966f8',
        color: '#fff',
        '& .MuiPickersToolbar-title': { color: '#fff' }
      }
    },
    // v9 has no MuiPickersCalendarHeader.switchHeader slot — the original v4
    // entry was empty anyway. The v4 dayLabel (day-of-week S/M/T/W/T/F/S label)
    // moved to MuiDayCalendar.weekDayLabel in v9.
    MuiDayCalendar: {
      weekDayLabel: {
        'font-size': '1.5rem'
      }
    },
    // x-date-pickers v9 renamed `MuiPickersDay` → `MuiPickerDay` (singular) and
    // collapsed the v4 day/daySelected/current slots into a single `root` slot
    // with state via the global `.Mui-selected` class.
    MuiPickerDay: {
      root: {
        color: '#3966f8',
        '&.Mui-selected': {
          color: '#fff'
        },
        // disablePast / disableFuture days carry Mui-disabled; without an explicit
        // override our root #3966f8 wins and they stay bright blue. Grey them out
        // to match standard MUI disabled-text affordance.
        '&.Mui-disabled': {
          color: 'rgba(0, 0, 0, 0.38)'
        }
      }
    },
    // x-date-pickers v9 replaced `MuiPickersYear` with `MuiYearCalendar`. The
    // year-button element is the `button` slot; selected state via `.Mui-selected`.
    MuiYearCalendar: {
      button: {
        'font-size': '1.4rem',
        // v9's selected year button renders a filled primary-colour background
        // (unlike v4's text-only selected state). Keep white text on that fill
        // — mirroring the MuiPickerDay override above. The old v4 #3966f8 +
        // 3rem produced blue-on-blue invisible text inside an oversized pill.
        '&.Mui-selected': {
          color: '#fff'
        }
      }
    },
    // MuiPickersModal: {
    //   dialogAction: {
    //     color: '#3966f8'
    //   }
    // },
    // MuiDialogActions: {
    //   spacing: {
    //     'font-size': '1.3rem'
    //   }
    // },
    MuiTypography: {
      colorTextPrimary: {
        color: '#ffffff'
      },
      // title: {
      //   'font-size': '1.575rem'
      // },
      // subheading: {
      //   'font-size': '1.2rem'
      // },
      body1: {
        'font-size': '1.38rem'
      },
      body2: {
        'font-size': '1.5rem',
        'font-weight': '500'
      },
      h4: {
        'font-size': '3.125rem'
      },
      h6: {
        'font-size': '1.8rem',
        color: '#3966f8'
      },
      subtitle1: {
        'font-size': '3rem'
      }
    },
    MuiChip: {
      root: {
        backgroundColor: 'unset'
      }
    },
    MuiSvgIcon: {
      // Under the 10px html root (htmlFontSize: 10), MUI's default icon rem sizes
      // render too small, so icons are pinned in px here. Previously a blanket
      // `root: { font-size: 24px }` was used — but in MUI v9 that root override wins
      // over the size variants, clamping `fontSize="small"` / `"large"` icons to
      // 24px (e.g. the financial-pyramid edit pens looked oversized). Size each
      // variant explicitly so the variants work again; medium stays 24px so the
      // vast majority of icons (no fontSize prop) are unchanged.
      fontSizeSmall: {
        fontSize: '20px'
      },
      fontSizeMedium: {
        fontSize: '24px'
      },
      fontSizeLarge: {
        fontSize: '35px'
      }
    },
    MuiTablePagination: {
      root: {
        color: 'rgba(0, 0, 0, 0.54)'
      }
    },
    MuiBadge: {
      colorError: {
        backgroundColor: '#ff0000'
      }
    },
    MuiStepIcon: {
      text: {
        fontSize: '1rem'
      }
    },
    MuiPaper: {
      root: {
        color: 'inherit'
      }
    },
    MuiSpeedDialAction: {
      staticTooltip: {
        whiteSpace: 'nowrap'
      }
    },
    MuiDialog: {
      root: {
        marginBottom: 'var(--ion-safe-area-bottom, 0px)'
      }
    }
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1340,
      xl: 1920
    }
  }
};

// A react-spring-bottom-sheet (BottomSheetOrDialog) holds an iOS
// body-scroll-lock that cancels touchmove on everything outside elements
// marked data-body-scroll-lock-ignore. Every Modal (Dialog, Drawer,
// SwipeableDrawer, Popover, Menu, mobile pickers) and Popper (Autocomplete,
// desktop pickers) portals to <body>, i.e. outside the sheet, so without
// this default anything opened on top of a sheet can't be scrolled on iPhone.
// Exported for the Soft Depth theme (src/design-system/theme.ts), which is
// built from scratch and needs the same default.
export const SCROLL_LOCK_IGNORE_COMPONENTS = {
  MuiModal: {
    defaultProps: { 'data-body-scroll-lock-ignore': 'true' }
  },
  MuiPopper: {
    defaultProps: { 'data-body-scroll-lock-ignore': 'true' }
  }
};

export const muiTheme = createTheme(adaptV4Theme(MUI_THEME_OPTIONS), {
  // adaptV4Theme only preserves the standard MUI palette keys (primary, secondary,
  // error, warning, info, success, text, background, divider). Custom keys like
  // `tertiary` are silently dropped, so re-attach them here via createTheme's
  // deep merge.
  palette: {
    tertiary: PALETTE.tertiary
  },
  components: {
    ...SCROLL_LOCK_IGNORE_COMPONENTS,
    // v4 Button default color was "default" (dark/gray text). v5+ removed
    // color="default" and now defaults to color="primary" (blue). Any <Button>
    // without an explicit color prop therefore turns blue in v9.
    // Restore v4 behavior by defaulting to "inherit" so unstyled buttons pick
    // up the surrounding text color. Buttons with explicit color="primary" etc.
    // are unaffected.
    MuiButton: {
      defaultProps: {
        color: 'inherit'
      }
    },
    // v4 default IconButton color was action.active (dark grey) regardless of
    // parent text colour. v9 dropped `color="default"` semantics and a bare
    // IconButton inherits from its parent — which flips it to white inside an
    // <AppBar color="primary">, to red inside <Typography color="error">, etc.
    // Restore v4 parity via styleOverrides gated on the absence of an explicit
    // `color` prop, so IconButtons with color="primary" / "inherit" stay
    // unaffected.
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          ...((!ownerState.color || ownerState.color === 'default') && {
            color: theme.palette.action.active
          })
        })
      }
    },
    // The v4 theme turned <Typography color="textPrimary"> white via the
    // `colorTextPrimary` override above. v9 has no such slot, so adaptV4Theme
    // drops it and the text falls back to dark grey — e.g. the white initial on
    // the green profile avatar turned black. Every textPrimary caller predates
    // the migration and sits on a coloured background, so restore v4 parity.
    MuiTypography: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.color === 'textPrimary' && { color: '#ffffff' })
        })
      }
    },
    MuiGrid: {
      styleOverrides: {
        // v4 Grid containers defaulted to width: 100%; v9 Grid v2 containers
        // default to width: auto (shrink to content). Restore the v4 behavior,
        // but skip it when the Grid also carries a `size` (nested container+item).
        // Note: Grid v2 always sets ownerState.size to an object ({} when unset),
        // so check for an empty object rather than undefined.
        root: ({ ownerState }) => {
          const size = ownerState.size;
          const hasSize = size && typeof size === 'object' && Object.keys(size).length > 0;
          return {
            ...(ownerState.container && !hasSize && { width: '100%' })
          };
        }
      }
    },
    // MUI v4 defaulted TextField/Select/FormControl to the `standard` variant
    // (underline). v5+ changed the default to `outlined` (boxed). Restore v4.
    // Left-anchored side-nav width override. Written as a v9-native function
    // form (ownerState-gated) because the equivalent v4 `paperAnchorLeft` slot
    // is silently dropped by adaptV4Theme in v9. Other anchors (right/top/bottom)
    // are unaffected and continue to size to content — e.g. the .todo / template
    // editor drawer needs its own min-width: 300px to keep the 提交 button visible.
    MuiDrawer: {
      styleOverrides: {
        // overflowX:hidden prevents a horizontal scrollbar caused by
        // .sideMenuItem having width:100% + margin-left:18px (= 268px content
        // in a 250px drawer).
        paper: ({ ownerState }) => ({
          overflowX: 'hidden',
          ...(ownerState.anchor === 'left' && {
            width: '250px'
          })
        })
      }
    },
    MuiTextField: {
      defaultProps: { variant: 'standard' }
    },
    MuiSelect: {
      defaultProps: { variant: 'standard' },
      // MUI v9 outlined Select has ~16.5px vertical padding vs v4's ~8px.
      // Restore compact padding globally for outlined Selects.
      styleOverrides: {
        outlined: {
          padding: '8px 32px 8px 14px'
        }
      }
    },
    MuiFormControl: {
      defaultProps: { variant: 'standard' }
    },
    // v4 InputLabel transforms only make sense for the standard variant.
    // Outlined labels must keep default MUI v5 positioning (inside the box →
    // float to the border notch on focus).
    MuiInputLabel: {
      styleOverrides: {
        shrink: ({ ownerState }) => ({
          ...(ownerState.variant !== 'outlined' && {
            transform: 'translate(0, 1.5px) scale(1)'
          })
        }),
        formControl: ({ ownerState }) => ({
          ...(ownerState.variant !== 'outlined' && {
            transform: 'translate(0, 12px) scale(1)'
          })
        })
      }
    },
    // x-date-pickers v9 uses its own PickersTextField / PickersOutlinedInput
    // instead of standard TextField. The global MuiFormControl font-size:inherit
    // cascades down and shrinks the picker text to ~10px. Fix by giving pickers
    // a sensible base font-size and proper input sizing.
    MuiPickersTextField: {
      styleOverrides: {
        root: {
          fontSize: 'inherit'
        }
      }
    },
    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: {
          fontSize: 'inherit',
          padding: '4px 8px',
          // Override the default 16.5px vertical padding on the sections
          // container. Using a nested selector for guaranteed specificity
          // over the component's own styled() defaults.
          '& .MuiPickersOutlinedInput-sectionsContainer': {
            padding: '4px 0'
          }
        },
        input: {
          fontSize: 'inherit',
          padding: '4px 0',
          height: 'auto'
        },
        notchedOutline: {
          borderColor: 'rgba(0, 0, 0, 0.23)'
        }
      }
    },
    MuiPickersInputBase: {
      styleOverrides: {
        root: {
          fontSize: 'inherit',
          '& .MuiPickersInputBase-sectionsContainer': {
            padding: '4px 0'
          }
        },
        input: {
          fontSize: 'inherit',
          padding: '4px 0',
          height: 'auto'
        }
      }
    },
    // Use dense format so section placeholders show "MMM" not "MMMM".
    MuiDesktopDatePicker: {
      defaultProps: {
        formatDensity: 'dense'
      }
    },
    MuiMobileDatePicker: {
      defaultProps: {
        formatDensity: 'dense'
      }
    },
    MuiDatePicker: {
      defaultProps: {
        formatDensity: 'dense'
      }
    },
    MuiDesktopDateTimePicker: {
      defaultProps: {
        formatDensity: 'dense'
      }
    },
    MuiMobileDateTimePicker: {
      defaultProps: {
        formatDensity: 'dense'
      }
    },
    MuiDateTimePicker: {
      defaultProps: {
        formatDensity: 'dense'
      }
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontSize: '10px',
          height: '16px',
          minWidth: '16px',
          padding: '0 4px'
        },
        dot: {
          height: '8px',
          minWidth: '8px',
          padding: 0
        }
      }
    },
    // v9 AccordionSummary expandIconWrapper uses alignItems:'normal' by default,
    // which causes the SVG icon to sit ~5px below center via flex baseline drift.
    MuiAccordionSummary: {
      styleOverrides: {
        expandIconWrapper: {
          alignItems: 'center'
        }
      }
    }
  }
});
